import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { SearchCheck, Loader2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => v === null || v === undefined ? "—" : `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const DayCountAuditCard = ({ token }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/finance/day-count-audit`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResult(data);
      toast.success(data.flagged_count === 0 ? "No bookings flagged — nothing looks affected." :
        `${data.flagged_count} booking${data.flagged_count !== 1 ? "s" : ""} flagged for your review.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Audit failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="day-count-audit-card" className="rounded-2xl border border-white/10 bg-[#0C0C0C] p-5 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display font-bold text-white flex items-center gap-2"><SearchCheck size={18} className="text-[#F2B705]" /> Day-Count Audit — Review Only</h3>
          <p className="text-white/45 text-sm mt-0.5">Read-only scan for bookings priced with the retired ceil-of-hours day-count rule. Nothing is changed automatically — review each one and correct case-by-case.</p>
        </div>
        <button data-testid="day-count-audit-run-btn" onClick={run} disabled={busy}
          className="bg-[#F2B705] text-black font-bold text-xs uppercase tracking-wide px-4 py-2.5 rounded-lg hover:bg-white transition-colors disabled:opacity-60 flex items-center gap-2">
          {busy ? <Loader2 className="animate-spin" size={14} /> : <SearchCheck size={14} />} Run Audit
        </button>
      </div>

      {result && (
        <>
          <div data-testid="day-count-audit-summary" className="rounded-xl border border-white/10 bg-[#0A0A0A] px-4 py-3 flex items-center justify-between">
            <span className="text-white/70 text-sm">Scanned {result.scanned} non-cancelled bookings</span>
            <span className={`font-bold text-sm ${result.flagged_count > 0 ? "text-amber-400" : "text-emerald-400"}`}>
              {result.flagged_count} flagged · est. total impact {money(result.total_estimated_overcharge)}
            </span>
          </div>

          {result.flagged_count > 0 && (
            <div className="rounded-xl border border-white/10 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-widest text-white/40 border-b border-white/10">
                    <th className="px-4 py-2.5">Renter</th>
                    <th className="px-4 py-2.5">Dates</th>
                    <th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5 text-right">Before</th>
                    <th className="px-4 py-2.5 text-right">Agreed Rate</th>
                    <th className="px-4 py-2.5 text-right">Correct 3-Day Calc</th>
                    <th className="px-4 py-2.5 text-right">Est. Overcharge</th>
                  </tr>
                </thead>
                <tbody>
                  {result.flagged_bookings.map((f) => (
                    <tr key={f.booking_id} data-testid={`day-count-audit-row-${f.booking_id}`} className="border-b border-white/5">
                      <td className="px-4 py-2.5 text-white/85 font-semibold">{f.name}<div className="text-white/35 text-[11px] font-normal">{f.vehicle}</div></td>
                      <td className="px-4 py-2.5 text-white/60 text-xs">{f.pickup_date} {f.pickup_time} → {f.return_date} {f.return_time}</td>
                      <td className="px-4 py-2.5 text-white/50 text-xs">{f.lifecycle_status} / {f.status}</td>
                      <td className="px-4 py-2.5 text-right text-white/80">{f.before.days}d · {money(f.before.total_price)}</td>
                      <td className="px-4 py-2.5 text-right text-white/60">{money(f.before.original_daily_rate)}/day</td>
                      <td className="px-4 py-2.5 text-right text-[#F2B705] font-semibold">{f.after_estimated.days}d · {money(f.after_estimated.total_price)}
                        {f.pricing_engine_cross_check != null && f.pricing_engine_cross_check !== f.after_estimated.total_price && (
                          <div className="text-white/30 text-[10px] font-normal">engine cross-check: {money(f.pricing_engine_cross_check)}</div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-right text-amber-400 font-bold">{money(f.estimated_overcharge)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-4 py-2.5 text-[11px] text-white/35 border-t border-white/5 flex items-start gap-1.5">
                <AlertTriangle size={12} className="mt-0.5 shrink-0 text-amber-400" />
                "Correct 3-Day Calc" = this booking's own agreed daily rate × the corrected day count — the most
                reliable figure, based on what was actually charged. The engine cross-check (when shown) re-quotes
                today's default pricing for comparison only and may legitimately differ. Verify against the full
                pricing snapshot (coverage, add-ons, fees) before approving anything — nothing here has been applied.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
